import { useCallback, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Instance, Resource } from '@detect/shared';
import { resourceService } from '../../services/resources';
import type { WithId } from '../../services/types';
import { useTenant } from '../../contexts/TenantContext';
import { useTenantQuery } from '../../hooks/useApi';
import { useCursorList } from '../../hooks/useCursorList';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import { clientService } from '../../services/clients';
import { collectorService } from '../../services/collectors';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';
import { formatRelativeTime, formatDateTime } from '../../utils/formatters';
import { PropertiesEditor } from './PropertiesEditor';
import { propertiesPatch, rowsFromProperties, type PropertyRow, type RowErrors } from './customProperties';
import { collectorOptionLabel } from '../collectors/channel';

export function ResourceDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { tenantId, hasRole } = useTenant();
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    rollup: Record<string, number | undefined>;
    regions: Record<string, { available: number; response_ms: number; status_code: number }>;
    probersUnavailable: boolean;
  } | null>(null);


  const query = useTenantQuery<WithId<Resource>>(
    ['resource', id],
    `/resources/${id}`
  );

  const fetchInstances = useCallback(
    (tid: string, startAfter: string | undefined) =>
      resourceService.listInstances(tid, id, { limit: 50, startAfter }),
    [id]
  );
  const instances = useCursorList<WithId<Instance>>(fetchInstances, id);

  if (query.isLoading) return <LoadingSpinner />;
  if (query.isError || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const resource = query.data;

  const remove = async () => {
    if (!tenantId) return;
    if (!window.confirm(`Delete ${resource.name}? Monitoring stops immediately.`)) {
      return;
    }
    setDeleting(true);
    setActionError(null);
    try {
      await resourceService.remove(tenantId, resource.id);
      navigate('/resources');
    } catch (err) {
      setActionError(err);
      setDeleting(false);
    }
  };


  const testNow = async () => {
    if (!tenantId) return;
    setActionError(null);
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(
        await resourceService.checkNow(tenantId, resource.id)
      );
    } catch (err) {
      setActionError(err);
    } finally {
      setTesting(false);
    }
  };

  const retire = async (instanceId: string) => {
    if (!tenantId) return;
    setActionError(null);
    try {
      await resourceService.retireInstance(tenantId, resource.id, instanceId);
      void instances.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <Link to="/resources">Resources</Link> / {resource.displayName ?? resource.name}
        </h1>
        {hasRole('agent') && (
          <div className="page-actions">
            {resource.kind === 'website' && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={testing}
                onClick={() => void testNow()}
              >
                {testing ? 'Testing…' : 'Test now'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditOpen(true)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={deleting}
              onClick={() => void remove()}
            >
              Delete
            </button>
          </div>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}
      {testResult !== null && (
        <div
          className={testResult.rollup.available === 1 ? 'notice' : 'error-state'}
          role="status"
        >
          {testResult.probersUnavailable ? (
            'Probes are temporarily unavailable — try again shortly.'
          ) : (
            <>
              {testResult.rollup.available === 1 ? 'Site is UP' : 'Site is DOWN'}
              {' — '}
              {Object.entries(testResult.regions)
                .map(
                  ([region, r]) =>
                    `${region.toUpperCase()}: ${r.available === 1 ? 'up' : 'down'} ` +
                    `(${Math.round(r.response_ms)} ms, HTTP ${r.status_code})`
                )
                .join(' · ')}
              {typeof testResult.rollup.ssl_days_remaining === 'number' &&
                ` · SSL expires in ${testResult.rollup.ssl_days_remaining} days`}
            </>
          )}
        </div>
      )}

      <div className="card">
        <dl className="detail-grid">
          <dt>Kind</dt>
          <dd>{resource.kind}</dd>
          <dt>{resource.kind === 'website' ? 'URL' : 'Address'}</dt>
          <dd>
            {resource.kind === 'website'
              ? resource.website?.url ?? '—'
              : resource.address ?? '—'}
          </dd>
          <dt>Monitoring</dt>
          <dd>{resource.monitoringEnabled ? 'enabled' : 'disabled'}</dd>
          <dt>Groups</dt>
          <dd>{resource.groups.length > 0 ? resource.groups.join(', ') : '—'}</dd>
          <dt>Last data</dt>
          <dd>{resource.lastDataAt ? formatRelativeTime(resource.lastDataAt) : 'never'}</dd>
          <dt>Description</dt>
          <dd>{resource.description ?? '—'}</dd>
        </dl>
      </div>

      <div className="section-heading-row">
        <h2>Properties</h2>
        {hasRole('agent') && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setEditOpen(true)}
          >
            Edit properties
          </button>
        )}
      </div>
      <div className="card">
        {Object.keys(resource.properties).length === 0 ? (
          <EmptyState title="No properties" />
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(resource.properties).map(([key, value]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{String(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <h2>Instances</h2>
      {instances.error ? (
        <ErrorState error={instances.error} onRetry={() => void instances.reload()} />
      ) : instances.loading ? (
        <LoadingSpinner />
      ) : instances.items.length === 0 ? (
        <EmptyState
          title="No instances"
          description="Discovery populates instances automatically once sensors run."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Discovered</th>
                <th>Last seen</th>
                {hasRole('agent') && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {instances.items.map((i) => (
                <tr key={i.id}>
                  <td>{i.displayName ?? i.name}</td>
                  <td>{i.status}</td>
                  <td>{i.autoDiscovered ? 'auto' : 'manual'}</td>
                  <td>{formatDateTime(i.lastSeenAt)}</td>
                  {hasRole('agent') && (
                    <td>
                      {i.status === 'active' && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => void retire(i.id)}
                        >
                          Retire
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={instances.hasMore}
        loadingMore={instances.loadingMore}
        loadMoreError={instances.loadMoreError}
        onLoadMore={() => void instances.loadMore()}
      />

      {editOpen && (
        <EditResourceModal
          resource={resource}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

export function EditResourceModal({
  resource,
  onClose,
  onSaved,
}: {
  resource: WithId<Resource>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId, clientScope } = useTenant();
  const [displayName, setDisplayName] = useState(
    resource.displayName ?? resource.name
  );
  const [description, setDescription] = useState(resource.description ?? '');
  const [address, setAddress] = useState(resource.address ?? '');
  const [groups, setGroups] = useState(resource.groups.join(', '));
  const [monitoring, setMonitoring] = useState(resource.monitoringEnabled);
  const [clientId, setClientId] = useState(resource.clientId ?? '');
  const [collectorId, setCollectorId] = useState(resource.collectorId ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(resource.website?.url ?? '');
  const [propertyRows, setPropertyRows] = useState<PropertyRow[]>(() =>
    rowsFromProperties(resource.properties)
  );
  const [propertyErrors, setPropertyErrors] = useState<Record<number, RowErrors>>({});
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const clientsQuery = usePickerOptions(['clients'], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter })
  );
  const collectorsQuery = usePickerOptions(['collectors'], (tid, startAfter) =>
    collectorService.list(tid, { limit: 100, startAfter })
  );
  const clients = (clientsQuery.data?.items ?? []).filter(
    (c) => clientScope === null || clientScope.includes(c.id)
  );
  // Collector/client affinity (API-enforced): shared collectors always
  // valid; client-bound ones only for that client.
  const collectors = (collectorsQuery.data?.items ?? []).filter(
    (c) => c.clientId === null || (clientId !== '' && c.clientId === clientId)
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    // Validate custom properties first: the same rules the API enforces
    // (custom.* only), so a doomed save never leaves the form.
    const props = propertiesPatch(resource.properties, propertyRows);
    if (!props.ok) {
      setPropertyErrors(props.errors);
      return;
    }
    setPropertyErrors({});
    setSubmitting(true);
    setError(null);
    try {
      await resourceService.update(tenantId, resource.id, {
        ...props.patch,
        displayName: displayName || null,
        description: description || null,
        clientId: clientId || null,
        ...(resource.kind === 'device'
          ? { address, ...(collectorId ? { collectorId } : {}) }
          : {}),
        ...(resource.kind === 'website' && resource.website
          ? { website: { ...resource.website, url: websiteUrl } }
          : {}),
        groups: groups
          .split(',')
          .map((g) => g.trim())
          .filter((g) => g.length > 0),
        monitoringEnabled: monitoring,
      });
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Edit ${resource.name}`} onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="edit-displayName">Name</label>
          <input
            id="edit-displayName"
            name="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="edit-client">Client</label>
          <select
            id="edit-client"
            name="clientId"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          >
            <option value="">— none (tenant-level) —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {resource.kind === 'device' && (
          <>
            <div className="form-field">
              <label htmlFor="edit-collector">Collector</label>
              <select
                id="edit-collector"
                name="collectorId"
                required
                value={collectorId}
                onChange={(e) => setCollectorId(e.target.value)}
              >
                <option value="">— select —</option>
                {collectors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {collectorOptionLabel(c, true)}
                  </option>
                ))}
              </select>
              {collectorsQuery.data?.truncated && (
                <p className="text-muted">Collector list truncated.</p>
              )}
            </div>
            <div className="form-field">
              <label htmlFor="edit-address">Address</label>
              <input
                id="edit-address"
                name="address"
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
          </>
        )}
        {resource.kind === 'website' && (
          <div className="form-field">
            <label htmlFor="edit-url">URL</label>
            <input
              id="edit-url"
              name="websiteUrl"
              type="url"
              required
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </div>
        )}
        <div className="form-field">
          <label htmlFor="edit-groups">Groups (comma-separated)</label>
          <input
            id="edit-groups"
            name="groups"
            value={groups}
            onChange={(e) => setGroups(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="edit-description">Description</label>
          <textarea
            id="edit-description"
            name="description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <PropertiesEditor
          idPrefix="edit-prop"
          rows={propertyRows}
          onChange={(rows) => {
            setPropertyRows(rows);
            setPropertyErrors({});
          }}
          errors={propertyErrors}
          managed={resource.properties}
        />
        <div className="form-field form-field-inline">
          <input
            id="edit-monitoring"
            name="monitoringEnabled"
            type="checkbox"
            checked={monitoring}
            onChange={(e) => setMonitoring(e.target.checked)}
          />
          <label htmlFor="edit-monitoring">Monitoring enabled</label>
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </form>
    </Modal>
  );
}
